import { isBoolean } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import FocusLock from "react-focus-lock";
import { useTranslation } from "react-i18next";
import { TfaMethod } from "@/core/constants/tfaMethod";
import AuthMpProvider from "@/core/data-provider/data-mp/auth/auth-mp-provider";
import TfaMpProvider from "@/core/data-provider/data-mp/tfa/tfa-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { ValidateCode } from "@/shared/ui";
import { AdditionalFlow } from "@/domains/auth/constants/additionalFlow";
import { enableTfaSelector, socialClubSelector } from "@/domains/auth/slice";
import useGetCodeTimer from "@/domains/auth/hooks/useGetCodeTimer";
import useSignInTfa from "@/domains/auth/hooks/useSignInTfa";
import { useAuthFlow } from "@/domains/auth/containers/context";
import AuthSocialClub from "@/domains/auth/components/AuthSocialClub";
import AuthTitle from "@/domains/auth/components/AuthTitle";

export default function AuthSignInTfaContainer() {
  const { enableTfa } = useAuthFlow();
  const socialClub = useAppSelector(socialClubSelector);
  const { openOnly } = useAuthFlow();
  const loginData = useAppSelector(enableTfaSelector);
  const { t } = useTranslation("auth");
  const [googleCode, setGoogleCode] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const { getTimer: getTimerEmail, timer: timerEmail } = useGetCodeTimer();
  const { getTimer: getTimerSMS, timer: timerSMS } = useGetCodeTimer();
  const {
    checkCodeHandler: checkEmailCode,
    sendCodeHandler: sendEmailCode,
    checkIsPending: checkEmailIsPending,
    checkIsCompleted: checkEmailIsCompleted,
    sendIsPending: sendEmailIsPending,
    sendError: sendEmailCodeError,
  } = useSignInTfa("email");
  const {
    checkCodeHandler: checkGoogleCode,
    checkIsPending: checkGoogleIsPending,
    checkIsCompleted: checkGoogleIsCompleted,
  } = useSignInTfa("google");
  const {
    checkCodeHandler: checkSmsCode,
    sendCodeHandler: sendSmsCode,
    checkIsPending: checkSmsIsPending,
    checkIsCompleted: checkSmsIsCompleted,
    sendIsPending: sendSmsIsPending,
    sendError: sendSmsCodeError,
  } = useSignInTfa("sms");

  const checkGoogleCodeHandler = useCallback(() => {
    checkGoogleCode(googleCode);
  }, [checkGoogleCode, googleCode]);

  const checkEmailCodeHandler = useCallback(() => {
    checkEmailCode(emailCode);
  }, [checkEmailCode, emailCode]);

  const sendEmailCodeHandler = useCallback(async () => {
    if (loginData.email) {
      await sendEmailCode(loginData.email);
      await getTimerEmail({ type: "email", payload: loginData.email });
    }
  }, [sendEmailCode, loginData.email]);

  const checkSmsCodeHandler = useCallback(() => {
    checkSmsCode(smsCode);
  }, [checkSmsCode, smsCode]);

  const sendSmsCodeHandler = useCallback(async () => {
    if (loginData.phoneNumber) {
      await sendSmsCode(loginData.phoneNumber);
      await getTimerSMS({ type: "sms", payload: loginData.phoneNumber });
    }
  }, [sendSmsCode, loginData.phoneNumber]);

  const isAllRight = useMemo(() => {
    let availiableTfaCount = 0;
    let sumControl = 0;
    if (enableTfa.isGoogleEnabled) {
      availiableTfaCount += 1;
      if (checkGoogleIsCompleted) {
        sumControl += 1;
      }
    }
    if (enableTfa.isSmsEnabled) {
      availiableTfaCount += 1;
      if (checkSmsIsCompleted) {
        sumControl += 1;
      }
    }
    if (enableTfa.isEmailEnabled) {
      availiableTfaCount += 1;
      if (checkEmailIsCompleted) {
        sumControl += 1;
      }
    }

    return availiableTfaCount && availiableTfaCount === sumControl;
  }, [checkSmsIsCompleted, checkGoogleIsCompleted, checkEmailIsCompleted, enableTfa]);

  const nextLogIn = useCallback(async () => {
    try {
      const response = await TfaMpProvider.nextLogIn();

      if (response instanceof RpcMpProviderSuccessResponse) {
        await AuthMpProvider.goToCreatePin();
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    if (loginData.email) {
      getTimerEmail({ type: "email", payload: loginData.email });
    }
  }, [loginData.email]);

  useEffect(() => {
    if (loginData.phoneNumber) {
      getTimerSMS({ type: "sms", payload: loginData.phoneNumber });
    }
  }, [loginData.phoneNumber]);

  useEffect(() => {
    if (isAllRight) {
      nextLogIn();
    }
  }, [isAllRight, nextLogIn]);

  const error = useMemo(
    () => sendEmailCodeError || sendSmsCodeError,
    [sendEmailCodeError, sendSmsCodeError],
  );

  const isValidGoogleCode = useMemo(
    () => (isBoolean(checkGoogleIsCompleted) ? checkGoogleIsCompleted : undefined),
    [checkGoogleIsCompleted],
  );
  const isValidEmailCode = useMemo(
    () => (isBoolean(checkEmailIsCompleted) ? checkEmailIsCompleted : undefined),
    [checkEmailIsCompleted],
  );
  const isValidSmsCode = useMemo(
    () => (isBoolean(checkSmsIsCompleted) ? checkSmsIsCompleted : undefined),
    [checkSmsIsCompleted],
  );

  const resetAccessTfaGoogle = useCallback(() => {
    openOnly(AdditionalFlow.AuthResetTfaAuthenticator);
  }, [openOnly]);
  const resetAccessTfaEmail = useCallback(() => {
    openOnly(AdditionalFlow.AuthResetTfaEmail);
  }, [openOnly]);
  const resetAccessTfaSMS = useCallback(() => {
    openOnly(AdditionalFlow.AuthResetTfaSMS);
  }, [openOnly]);

  return (
    <>
      <AuthTitle>{t("auth.tfaVerification")}</AuthTitle>
      {socialClub && <AuthSocialClub socialClub={socialClub} />}
      <FocusLock className="flex flex-col grow">
        {enableTfa.isGoogleEnabled && (
          <ValidateCode
            type={TfaMethod.AUTHENTICATOR}
            address=""
            onSubmit={checkGoogleCodeHandler}
            onSendCode={() => {}}
            code={googleCode}
            onCodeChange={setGoogleCode}
            pending={checkGoogleIsPending}
            sendCodePending={false}
            className="grow-0"
            showSubmitButton={false}
            isValid={isValidGoogleCode}
            onResetAccess={resetAccessTfaGoogle}
          />
        )}
        {enableTfa.isEmailEnabled && (
          <ValidateCode
            type={TfaMethod.EMAIL}
            address={loginData.email}
            onSubmit={checkEmailCodeHandler}
            onSendCode={sendEmailCodeHandler}
            code={emailCode}
            onCodeChange={setEmailCode}
            pending={checkEmailIsPending}
            sendCodePending={sendEmailIsPending}
            className="mt-5 grow-0"
            showSubmitButton={false}
            isValid={isValidEmailCode}
            onResetAccess={resetAccessTfaEmail}
            timer={timerEmail}
          />
        )}
        {enableTfa.isSmsEnabled && (
          <ValidateCode
            type={TfaMethod.SMS}
            address={loginData.phoneNumber}
            onSubmit={checkSmsCodeHandler}
            onSendCode={sendSmsCodeHandler}
            code={smsCode}
            onCodeChange={setSmsCode}
            pending={checkSmsIsPending}
            sendCodePending={sendSmsIsPending}
            className="mt-5 grow-0"
            showSubmitButton={false}
            isValid={isValidSmsCode}
            onResetAccess={resetAccessTfaSMS}
            timer={timerSMS}
          />
        )}
        <div className="mt-auto grid gap-2.5">
          {!!error && <div className="alert alert--danger">*{error}</div>}{" "}
        </div>
      </FocusLock>
    </>
  );
}
